<script setup lang="ts">
/**
 * Overlay for MuseScore-style note annotations: renders each placed
 * annotation as text directly above its note, and — while a cell is being
 * dragged (from AnnotationPane or an existing annotation being moved) — a
 * dashed line from the pointer to whichever note is currently closest.
 *
 * Lives as an absolute-positioned sibling inside `.sheet-music-container`
 * (see GameAreaSheetMusic), the same pattern SheetMusicSectionsOverlay uses:
 * left/top are in the sheet's local (unscaled) px, `scrollOffset` added in by
 * hand since this div isn't inside the transform that scrolls the sheet, and
 * the parent's own `transform: scale(zoom)` takes care of scaling this
 * overlay (and therefore the annotation text) for free — in single-line
 * mode. Multi-line mode has no such transform (OSMD re-renders natively per
 * zoom level instead), so there the `textScale` prop drives the annotation
 * pill's font-size/padding/radius directly; see its doc comment below.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { MeasureBound, Note } from '@/types';
import { toLogicalRect } from '@/utils/screenRotation';
import { getMeasureBoundForIndex } from '@/utils';
import {
  findNearestNoteWithinProximity,
  useInjectNoteAnnotations,
  type NoteAnnotation,
} from '@/composables/useNoteAnnotations';

const props = withDefaults(defineProps<{
  notes: Note[];
  /**
   * Per-measure geometry (staff top/height), used so every *placed*
   * annotation anchors to its measure's staff top — a single, row-consistent
   * height — instead of its note's own y, which otherwise bobs up/down with
   * pitch. Falls back to the note's own y if its measure isn't in here yet.
   */
  measureBounds: MeasureBound[];
  scrollOffset: number;
  scale?: number;
  /**
   * Multi-line mode renders a spacer div (`multiSpacerPx`) above the actual
   * OSMD content inside `.sheet-music-stage`, so this overlay (a sibling
   * positioned `inset: 0` on that same stage) starts above where the notes
   * actually are. Both `note.y` (computeOsmdNoteCoordinates) and
   * `measureBounds[i].y` (computeOsmdMeasureBounds) are measured
   * self-relative to the OSMD div and are equally oblivious to that spacer,
   * so both need this added by hand (see annotationAnchorY/localPointFromScreen
   * below). Single-line mode has no such spacer (defaults to 0, so the
   * distinction is moot there).
   */
  verticalOffset?: number;
  /**
   * Multiplier for the annotation text's own size (font-size/padding), kept
   * separate from `scale`. In single-line mode the whole stage — this
   * overlay included — is CSS `transform: scale(...)`'d by the parent, so
   * the text already grows for free and this should stay 1. In multi-line
   * mode OSMD re-renders natively per zoom level (no such transform), so
   * `scale` is fixed at 1 for position math, and this prop must be fed the
   * actual zoom instead for the text to keep up.
   */
  textScale?: number;
}>(), {
  scale: 1,
  verticalOffset: 0,
  textScale: 1,
});

const noteAnnotations = useInjectNoteAnnotations();

const rootEl = ref<HTMLElement>();

// The vertical gap between a note's top and the bottom of its annotation
// text, in the same local (unscaled) px as note.x/y. Base (textScale === 1)
// value — scaled by textScale below so the gap grows with the annotation
// text itself in multi-line mode instead of staying fixed while the text
// (and single-line mode's CSS transform) scale around it.
const ANNOTATION_GAP_PX = 16;

// note.x is the *left edge* of the notehead's own glyph rect (see
// computeOsmdNoteCoordinates), not its horizontal center — so centering the
// placed pill, the drag ghost's target ring, and the dashed line's note-end on
// note.x directly lands them all visibly left of the actual note. Nudge right
// by roughly half a notehead's width to compensate; there's no per-note width
// to read (only x/y are tracked), so this is a fixed approximation rather
// than an exact center.
const ANNOTATION_HORIZONTAL_OFFSET_PX = 5;

// Base (textScale === 1) sizing for the annotation pill. Scaled together so
// the padding/corner-radius stay proportional to the text at every zoom
// level, rather than the text alone growing inside a fixed-size box.
const ANNOTATION_FONT_PX = 16;
const ANNOTATION_PADDING_Y_PX = 1;
const ANNOTATION_PADDING_X_PX = 4;
const ANNOTATION_RADIUS_PX = 4;

const annotationTextStyle = computed(() => ({
  fontSize: `${ANNOTATION_FONT_PX * props.textScale}px`,
  padding: `${ANNOTATION_PADDING_Y_PX * props.textScale}px ${ANNOTATION_PADDING_X_PX * props.textScale}px`,
  borderRadius: `${ANNOTATION_RADIUS_PX * props.textScale}px`,
}));

// Shared by every place that lands something on note.x (pill, target ring,
// dashed line's note-end) — see ANNOTATION_HORIZONTAL_OFFSET_PX above.
const annotationHorizontalOffset = computed(() => ANNOTATION_HORIZONTAL_OFFSET_PX * props.textScale);

// Scaled the same way as annotationHorizontalOffset/annotationTextStyle —
// see ANNOTATION_GAP_PX above.
const annotationGap = computed(() => ANNOTATION_GAP_PX * props.textScale);

// Screen-space translation shared by every rendered element below, so the
// scrollOffset/verticalOffset/horizontal-nudge math (see prop doc comments)
// lives in one place instead of being repeated at each call site.
const toLocalX = (x: number): number => x + props.scrollOffset;
const toLocalY = (y: number): number => y + props.verticalOffset;
const noteAnchorX = (x: number): number => toLocalX(x) + annotationHorizontalOffset.value;

// A placed annotation's vertical anchor: its measure's staff top when known,
// so every annotation in a row lands at the same height regardless of which
// line/space its note's head sits on. Only for *placed* annotations — the
// drag ghost intentionally keeps following the pointer, and the dashed
// association line/ring still point at the note's own y (see dragTargetNote)
// so the user can see exactly which note it'll land on.
function annotationAnchorY(note: Note): number {
  const bound = getMeasureBoundForIndex(props.measureBounds, note.measureIndex);
  // Both the measure bound and the note.y fallback are self-relative to the
  // OSMD div (see verticalOffset's doc comment above), so both go through toLocalY.
  return toLocalY(bound?.y ?? note.y ?? 0);
}

// One entry per note that actually has a placed annotation — computed once
// so the template neither re-queries byNoteIndex per field nor renders a
// wrapper element for every unannotated note.
type PlacedAnnotation = { index: number; note: Note; annotation: NoteAnnotation };
const placedAnnotations = computed<PlacedAnnotation[]>(() => {
  const placed: PlacedAnnotation[] = [];
  props.notes.forEach((note, index) => {
    if (note.isTremoloRepeat) return;
    const annotation = noteAnnotations.byNoteIndex.value.get(index);
    if (annotation) placed.push({ index, note, annotation });
  });
  return placed;
});

function localPointFromScreen(screenPoint: { x: number; y: number }) {
  const root = rootEl.value;
  if (!root) return null;
  const rect = toLogicalRect(root.getBoundingClientRect());
  return {
    x: (screenPoint.x - rect.left) / props.scale - props.scrollOffset,
    y: (screenPoint.y - rect.top) / props.scale - props.verticalOffset,
  };
}

onMounted(() => {
  noteAnnotations.registerNoteResolver((screenPoint) => {
    const local = localPointFromScreen(screenPoint);
    if (!local) return null;
    return findNearestNoteWithinProximity(props.notes, local);
  });
});

onBeforeUnmount(() => noteAnnotations.registerNoteResolver(null));

// Dashed-line + nearest-note highlight while dragging — purely visual, so
// recomputed straight off the reactive pointer position rather than routed
// through the resolver (that's a plain function, called only at drop time).
// Uses the same proximity cutoff as the resolver (findNearestNoteWithinProximity),
// so the line only ever promises a landing that the actual drop will honor.
const dragTargetNote = ref<{ x: number; y: number } | null>(null);
const dragLocalPoint = ref<{ x: number; y: number } | null>(null);

function updateDragVisuals() {
  const screenPoint = noteAnnotations.pointerScreen.value;
  if (!screenPoint) {
    dragTargetNote.value = null;
    dragLocalPoint.value = null;
    return;
  }
  const local = localPointFromScreen(screenPoint);
  dragLocalPoint.value = local;
  const nearest = local ? findNearestNoteWithinProximity(props.notes, local) : null;
  dragTargetNote.value = nearest ? { x: nearest.x, y: nearest.y } : null;
}

// Cheap enough (one pass over notes) to run on every pointer move directly —
// no separate throttling needed at this scale of note count.
watch(() => noteAnnotations.pointerScreen.value, updateDragVisuals);
</script>

<template>
  <div
    ref="rootEl"
    class="absolute inset-0 pointer-events-none"
  >
    <!-- Placed annotations, one per note that has one. -->
    <div
      v-for="placed in placedAnnotations"
      :key="`annotation-${placed.index}`"
      class="note-annotation pointer-events-auto"
      :style="{
        left: `${noteAnchorX(placed.note.x)}px`,
        top: `${annotationAnchorY(placed.note) - annotationGap}px`,
        ...annotationTextStyle,
      }"
      @pointerdown.stop="(e) => noteAnnotations.startDragExisting(placed.annotation, e)"
      @mousedown.stop
      @touchstart.stop
    >
      {{ placed.annotation.text }}
    </div>

    <!-- Dashed association line + ghost, while a cell is being dragged. -->
    <template v-if="noteAnnotations.isDragging.value && dragLocalPoint">
      <svg
        v-if="dragTargetNote"
        class="absolute inset-0 w-full h-full overflow-visible"
      >
        <line
          :x1="toLocalX(dragLocalPoint.x)"
          :y1="toLocalY(dragLocalPoint.y)"
          :x2="noteAnchorX(dragTargetNote.x)"
          :y2="toLocalY(dragTargetNote.y)"
          class="note-annotation-link"
        />
        <circle
          :cx="noteAnchorX(dragTargetNote.x)"
          :cy="toLocalY(dragTargetNote.y)"
          r="10"
          class="note-annotation-target-ring"
        />
      </svg>
      <div
        class="note-annotation note-annotation-ghost"
        :style="{
          left: `${toLocalX(dragLocalPoint.x)}px`,
          top: `${toLocalY(dragLocalPoint.y)}px`,
          ...annotationTextStyle,
        }"
      >
        {{ noteAnnotations.dragPayload.value?.text }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.note-annotation {
  position: absolute;
  transform: translate(-50%, -100%);
  font-weight: 700;
  line-height: 1;
  color: var(--color-text-primary);
  background: var(--color-background);
  cursor: grab;
  user-select: none;
  touch-action: none;
  white-space: nowrap;
}

.note-annotation-ghost {
  opacity: 0.85;
  cursor: grabbing;
  pointer-events: none;
}

.note-annotation-link {
  stroke: var(--color-text-primary);
  stroke-width: 2;
  stroke-dasharray: 4 4;
}

.note-annotation-target-ring {
  fill: none;
  stroke: var(--color-text-primary);
  stroke-width: 2;
}
</style>
